import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, ChevronDown, MoreHorizontal } from 'lucide-react';
import clsx from 'clsx';
import type { Space } from '@/types';
import { useAppStore } from '@/store/appStore';
import { InlineEditInput } from './InlineEditInput';
import { handleError } from '@/utils/errorHandler';

interface SortableSpaceItemProps {
  space: Space;
  isEmpty: boolean;
  isActive: boolean;
  /** True when a child item (folder/list) is currently active */
  isAncestorActive?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

export function SortableSpaceItem({
  space,
  isEmpty,
  isActive,
  isAncestorActive,
  isExpanded,
  onToggle,
  onClick,
  onContextMenu,
  children,
}: SortableSpaceItemProps) {
  const editingItems = useAppStore((state) => state.editingItems);
  const savePendingItemName = useAppStore((state) => state.savePendingItemName);
  const setEditingItem = useAppStore((state) => state.setEditingItem);
  const spaces = useAppStore((state) => state.spaces);
  const setSpaces = useAppStore((state) => state.setSpaces);
  const isEditing = editingItems.has(space.id);
  
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: space.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} data-space-name={space.name}>
      {/* Space Header */}
      <div
        {...attributes}
        {...(isEditing ? {} : listeners)} // Don't apply drag listeners when editing
        onClick={isEmpty || isEditing ? undefined : onToggle}
        onContextMenu={onContextMenu}
        className={clsx(
          'w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] transition-colors group select-none',
          isEditing ? 'cursor-default' : isEmpty ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
          isActive
            ? 'bg-accent-violet-dim text-accent-violet'
            : isAncestorActive
              ? 'text-text-primary'
              : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
        )}
      >
        {!isEmpty && !isEditing && (
          <span className="text-text-quaternary group-hover:text-text-tertiary transition-colors shrink-0">
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </span>
        )}
        {(isEmpty || isEditing) && <span className="w-3 h-3 shrink-0" />}
        {isEditing ? (
          <InlineEditInput
            value={space.name}
            placeholder="Nome progetto..."
            onSave={async (name) => {
              try {
                await savePendingItemName(space.id, name);
              } catch (error) {
                handleError(error, { level: 'error', message: 'Errore nel salvataggio del nome del progetto' });
                setEditingItem(space.id, false);
              }
            }}
            onCancel={() => {
              setEditingItem(space.id, false);
              // Remove temporary space if cancelled
              const updatedSpaces = spaces.filter(s => s.id !== space.id);
              setSpaces(updatedSpaces);
            }}
            className="flex-1"
          />
        ) : (
          <>
        <span
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="flex-1 text-left font-medium truncate cursor-pointer hover:text-accent-violet transition-colors"
        >
          {space.name}
        </span>
        {/* Count / Menu — menu replaces count on hover */}
        <span className="relative flex items-center justify-end min-w-[20px]">
          <span className="text-[10px] text-text-quaternary tabular-nums font-medium group-hover:opacity-0 transition-opacity">
            {space.taskCount.toLocaleString()}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onContextMenu(e);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="absolute inset-0 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-surface-tertiary transition-all"
          >
            <MoreHorizontal className="w-3 h-3 text-text-quaternary" />
          </button>
        </span>
          </>
        )}
      </div>

      {/* Children (folders, lists, etc.) */}
      {children}
    </div>
  );
}
